import React, { useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import CategoryIcon from './CategoryIcon';
import ProviderPlaceChip from './ProviderPlaceChip';
import { Category } from '../types/foundObject';
import { FilterState } from './FoundObjectsFilters';
import ReferenceSuffixFilterField from './ReferenceSuffixFilterField';
import CategoryAttributeFilterControls from './CategoryAttributeFilterControls';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { useProviderInfo } from '../hooks/useProviderInfo';
import type { ProviderPlaceItem } from '../types/providerPlace';
import { comparePlacesByLabel } from '../utils/comparePlaceLabels';
import { isCentralPlaceStorageMode } from '../utils/placeStorageMode';

function toISODateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayLocal(): string {
  return toISODateLocal(new Date());
}

function addDaysLocal(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return toISODateLocal(dt);
}

function countAdvancedFilters(filters: FilterState): number {
  let count = 0;
  if (filters.provider_item_id) count++;
  if (filters.createdFrom) count++;
  if (filters.createdTo) count++;
  return count;
}

function computeDateToggleValue(f: FilterState): string | null {
  if (!f.dateFrom && !f.dateTo) {
    return 'all';
  }
  const t = todayLocal();
  if (f.dateFrom === t && f.dateTo === t) {
    return 'today';
  }
  const y = addDaysLocal(t, -1);
  if (f.dateFrom === y && f.dateTo === y) {
    return 'yesterday';
  }
  const b = addDaysLocal(t, -2);
  if (f.dateFrom === b && f.dateTo === b) {
    return 'before_yesterday';
  }
  const threeStart = addDaysLocal(t, -2);
  if (f.dateFrom === threeStart && f.dateTo === t) {
    return 'three_days';
  }

  return null;
}

function computeCreatedToggleValue(f: FilterState): string | null {
  if (!f.createdFrom && !f.createdTo) {
    return 'all';
  }
  const t = todayLocal();
  if (f.createdFrom === t && f.createdTo === t) {
    return 'today';
  }
  const weekStart = addDaysLocal(t, -6);
  if (f.createdFrom === weekStart && f.createdTo === t) {
    return 'week';
  }
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const monthEnd = toISODateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  if (f.createdFrom === monthStart && f.createdTo === monthEnd) {
    return 'month';
  }
  return null;
}

interface FoundObjectsTopFiltersProps {
  filters: FilterState;
  onFiltersChange: (f: FilterState) => void;
}

/**
 * Bandeau de filtres au-dessus de la grille (recherche globale, ref, dates rapides, catégorie, conversations).
 */
const FoundObjectsTopFilters: React.FC<FoundObjectsTopFiltersProps> = ({ filters, onFiltersChange }) => {
  const { data: categoriesData = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => response.data?.categories || [],
  });
  const categories = (categoriesData || []) as Category[];

  const { data: providerInfo } = useProviderInfo();
  const isCentralMode = isCentralPlaceStorageMode(providerInfo);

  const { data: providerItemsList = [] } = useQuery({
    queryKey: ['provider-items', providerInfo?.id],
    queryFn: async () => {
      const res = await apiService.getProviderItems(providerInfo!.id);
      return (res.data.provider_items || []) as ProviderPlaceItem[];
    },
    enabled: !!providerInfo?.id && !isCentralMode,
  });

  const activePlaces = useMemo(
    () =>
      providerItemsList
        .filter((p) => p.is_active !== false)
        .slice()
        .sort(comparePlacesByLabel),
    [providerItemsList]
  );

  const selectedCategory = filters.category
    ? categories.find((c) => c.name === filters.category)
    : undefined;

  const advancedActiveCount = countAdvancedFilters(filters);

  const set = (patch: Partial<FilterState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  useEffect(() => {
    if (!isCentralMode) return;
    if (!filters.provider_item_id && !filters.createdFrom && !filters.createdTo) return;
    onFiltersChange({
      ...filters,
      provider_item_id: '',
      createdFrom: '',
      createdTo: '',
    });
    // Intentional: reset lieu / période de création once when central mode is active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCentralMode]);

  /** Une seule date affichée : jour unique si from===to, sinon fin de plage. */
  const displayedDay =
    filters.dateFrom && filters.dateTo && filters.dateFrom === filters.dateTo
      ? filters.dateFrom
      : filters.dateTo || filters.dateFrom || '';

  const setSingleDay = (iso: string) => {
    if (!iso) {
      set({ dateFrom: '', dateTo: '' });
      return;
    }
    set({ dateFrom: iso, dateTo: iso });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Filtres
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Recherche globale"
        placeholder="Titre, destinataire, n° tracking…"
        value={filters.search}
        onChange={(e) => set({ search: e.target.value })}
        sx={{ mb: 1.5 }}
      />

      {!isCentralMode && (
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mb: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>
              Lieu et période de création
            </Typography>
            {advancedActiveCount > 0 ? (
              <Chip label={advancedActiveCount} size="small" color="primary" />
            ) : null}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {activePlaces.length > 0 ? (
            <FormControl fullWidth size="small">
              <InputLabel>Lieu</InputLabel>
              <Select
                value={filters.provider_item_id}
                label="Lieu"
                onChange={(e) => set({ provider_item_id: e.target.value })}
                renderValue={(selected) => {
                  if (!selected) {
                    return <em>Tous les lieux</em>;
                  }
                  const place = activePlaces.find((p) => String(p.id) === selected);
                  if (!place) return selected;
                  return (
                    <ProviderPlaceChip
                      place={place}
                      label={place.display_name || place.name}
                      size="small"
                    />
                  );
                }}
              >
                <MenuItem value="">
                  <em>Tous les lieux</em>
                </MenuItem>
                {activePlaces.map((place) => (
                  <MenuItem key={place.id} value={String(place.id)}>
                    <ProviderPlaceChip
                      place={place}
                      label={place.display_name || place.name}
                      size="small"
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Créé le
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <TextField
                type="date"
                size="small"
                label="Du"
                value={filters.createdFrom}
                onChange={(e) => set({ createdFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                size="small"
                label="Au"
                value={filters.createdTo}
                onChange={(e) => set({ createdTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={computeCreatedToggleValue(filters)}
                onChange={(_, v) => {
                  if (v == null) {
                    return;
                  }
                  const t = todayLocal();
                  if (v === 'all') {
                    set({ createdFrom: '', createdTo: '' });
                    return;
                  }
                  if (v === 'today') {
                    set({ createdFrom: t, createdTo: t });
                    return;
                  }
                  if (v === 'week') {
                    set({ createdFrom: addDaysLocal(t, -6), createdTo: t });
                    return;
                  }
                  if (v === 'month') {
                    const now = new Date();
                    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                    const monthEnd = toISODateLocal(new Date(now.getFullYear(), now.getMonth() + 1, 0));
                    set({ createdFrom: monthStart, createdTo: monthEnd });
                  }
                }}
              >
                <ToggleButton value="today">Aujourd&apos;hui</ToggleButton>
                <ToggleButton value="week">7 jours</ToggleButton>
                <ToggleButton value="month">Ce mois</ToggleButton>
                <ToggleButton value="all">Toutes</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      )}

      <ReferenceSuffixFilterField
        value={filters.referenceCode}
        onChange={(suffix) => set({ referenceCode: suffix })}
        providerId={providerInfo?.id}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Quand ?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <TextField
            type="date"
            size="small"
            value={displayedDay}
            onChange={(e) => setSingleDay(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
            label="Date"
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={computeDateToggleValue(filters)}
            onChange={(_, v) => {
              if (v == null) {
                return;
              }
              const t = todayLocal();
              if (v === 'all') {
                set({ dateFrom: '', dateTo: '' });
                return;
              }
              if (v === 'today') {
                set({ dateFrom: t, dateTo: t });
                return;
              }
              if (v === 'yesterday') {
                const y = addDaysLocal(t, -1);
                set({ dateFrom: y, dateTo: y });
                return;
              }
              if (v === 'before_yesterday') {
                const b = addDaysLocal(t, -2);
                set({ dateFrom: b, dateTo: b });
                return;
              }
              if (v === 'three_days') {
                set({ dateFrom: addDaysLocal(t, -2), dateTo: t });
              }
            }}
          >
            <ToggleButton value="today">Aujourd&apos;hui</ToggleButton>
            <ToggleButton value="yesterday">Hier</ToggleButton>
            <ToggleButton value="before_yesterday">Avant-hier</ToggleButton>
            <ToggleButton value="three_days">3 jours</ToggleButton>
            <ToggleButton value="all">Toutes</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220, flex: '1 1 200px' }}>
          <InputLabel>Catégories</InputLabel>
          <Select
            value={filters.category}
            label="Catégories"
            onChange={(e) =>
              set({
                category: e.target.value,
                attributes: {},
              })
            }
            renderValue={(selected) => {
              if (!selected) {
                return <em>Toutes</em>;
              }
              const cat = categories.find((c) => c.name === selected);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon
                    icon={cat?.icon}
                    customIconUrl={cat?.custom_icon_url}
                    customIconFilename={cat?.custom_icon_filename}
                    size="small"
                    color="primary.main"
                  />
                  {selected}
                </Box>
              );
            }}
          >
            <MenuItem value="">
              <em>Toutes</em>
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon
                    icon={category.icon}
                    customIconUrl={category.custom_icon_url}
                    customIconFilename={category.custom_icon_filename}
                    size="small"
                    color="primary.main"
                  />
                  {category.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedCategory ? (
        <CategoryAttributeFilterControls
          category={selectedCategory}
          attributeFilters={filters.attributes}
          onAttributeFiltersChange={(attributes) => set({ attributes })}
          compact
        />
      ) : null}
    </Paper>
  );
};

export default FoundObjectsTopFilters;
